import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import { downloadResource } from "../../lib/download";
import ClientTabs from "./ClientTabs";
import { useActiveClients } from "../../hooks/useActiveClients";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { toast } from "sonner";
import {
  Plus, ChevronDown, Trash2, Download, Paperclip, X, ListChecks,
  Layers, Save, Pencil, FileText, Check, Youtube, Lock, Unlock,
} from "lucide-react";

const fieldCls = "rounded-lg px-3 py-2 outline-none border focus:ring-2 focus:ring-[#F97316] text-sm";
const IMPL_STATUS = { "Not Started": "#6B7280", "In Progress": "#3B82F6", "Complete": "#10B981" };

function StatusPill({ status }) {
  const c = IMPL_STATUS[status] || "#6B7280";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${c}22`, color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} /> {status}
    </span>
  );
}

function TaskFields({ idPrefix, title, setTitle, instructions, setInstructions, sites, setSites, onSave, onCancel, saveLabel }) {
  return (
    <div className="space-y-2 p-3 bg-white rounded-lg border" style={{ borderColor: "#E5E7EB" }} data-testid={`task-form-${idPrefix}`}>
      <input data-testid={`task-title-${idPrefix}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      <textarea data-testid={`task-instructions-${idPrefix}`} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Task instructions" rows={2} className={`${fieldCls} w-full resize-y`} style={{ borderColor: "#E5E7EB" }} />
      <input data-testid={`task-sites-${idPrefix}`} value={sites} onChange={(e) => setSites(e.target.value)} placeholder="Sites, comma separated (e.g. Mondaleby, Broadlands, North Block)" className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      <div className="flex items-center gap-2">
        <button data-testid={`save-task-${idPrefix}`} onClick={onSave} className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#F97316" }}>{saveLabel}</button>
        {onCancel && <button data-testid={`cancel-task-${idPrefix}`} onClick={onCancel} className="text-sm text-[#6B7280] px-2 py-2">Cancel</button>}
      </div>
    </div>
  );
}

function TaskItem({ t, onEdit, onDelete }) {
  const c = IMPL_STATUS[t.status] || "#6B7280";
  return (
    <div data-testid={`admin-task-${t.id}`} className="bg-white rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium flex items-center gap-2">
            {t.title}
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: `${c}22`, color: c }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} />
              {t.status}
            </span>
          </p>
          {t.instructions && <p className="text-sm text-[#6B7280] mt-1 whitespace-pre-wrap">{t.instructions}</p>}
          <p className="text-xs text-[#6B7280] mt-2 mb-1">
            Sites completed: {(t.sites || []).filter((s) => s.completed).length}/{(t.sites || []).length}
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid={`admin-task-sites-${t.id}`}>
            {(t.sites || []).length === 0 && <span className="text-xs text-[#9CA3AF]">No sites</span>}
            {(t.sites || []).map((s) => (
              <span key={s.name} data-testid={`admin-site-${t.id}-${s.name}`}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                style={{ backgroundColor: s.completed ? "#DCFCE7" : "#F3F4F6", color: s.completed ? "#10B981" : "#6B7280" }}>
                {s.completed ? <Check size={11} /> : <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#9CA3AF" }} />}
                {s.name}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-shrink-0">
          <button data-testid={`edit-task-${t.id}`} onClick={() => onEdit(t)} title="Edit task" className="p-1.5 rounded hover:bg-gray-100 text-[#6B7280]"><Pencil size={15} /></button>
          <button data-testid={`delete-task-${t.id}`} onClick={() => onDelete(t)} className="p-1.5 rounded hover:bg-gray-100 text-red-500"><Trash2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function useTaskForm(stage, reload) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [sites, setSites] = useState("");

  const resetForm = () => { setTitle(""); setInstructions(""); setSites(""); setAdding(false); setEditingId(null); };

  const add = async () => {
    if (!title.trim()) { toast.error("Task title required"); return; }
    await api.post(`/implementation/stages/${stage.id}/tasks`, { title, instructions, sites });
    toast.success("Task added");
    resetForm(); reload();
  };
  const startEdit = (t) => {
    setAdding(false);
    setEditingId(t.id);
    setTitle(t.title);
    setInstructions(t.instructions || "");
    setSites((t.sites || []).map((s) => s.name).join(", "));
  };
  const saveEdit = async () => {
    if (!title.trim()) { toast.error("Task title required"); return; }
    await api.put(`/implementation/stages/${stage.id}/tasks/${editingId}`, { title, instructions, sites });
    toast.success("Task updated");
    resetForm(); reload();
  };
  const remove = async (t) => { await api.delete(`/implementation/stages/${stage.id}/tasks/${t.id}`); toast.success("Task removed"); reload(); };

  const toggleAdding = () => { if (!adding) { resetForm(); setAdding(true); } else setAdding(false); };
  const fieldProps = { title, setTitle, instructions, setInstructions, sites, setSites, onCancel: resetForm };
  return { adding, editingId, toggleAdding, add, startEdit, saveEdit, remove, fieldProps };
}

function TaskList({ stage, form }) {
  const tasks = stage.tasks || [];
  if (!tasks.length) return <p className="text-sm text-[#6B7280]">No tasks yet.</p>;
  return tasks.map((t) => (
    form.editingId === t.id ? (
      <TaskFields key={t.id} idPrefix={`edit-${t.id}`} {...form.fieldProps} onSave={form.saveEdit} saveLabel="Save Changes" />
    ) : (
      <TaskItem key={t.id} t={t} onEdit={form.startEdit} onDelete={form.remove} />
    )
  ));
}

function TaskManager({ stage, reload }) {
  const form = useTaskForm(stage, reload);
  return (
    <div className="mt-3 rounded-lg border p-4" style={{ borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" }}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-semibold flex items-center gap-2"><ListChecks size={16} style={{ color: "#F97316" }} /> Tasks</p>
        <button data-testid={`add-task-${stage.id}`} onClick={form.toggleAdding}
          className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#0A1F44" }}>
          <Plus size={15} /> Add New Task
        </button>
      </div>

      {form.adding && (
        <div className="mb-4">
          <TaskFields idPrefix={stage.id} {...form.fieldProps} onSave={form.add} saveLabel="Save Task" />
        </div>
      )}

      <div className="space-y-2">
        <TaskList stage={stage} form={form} />
      </div>
    </div>
  );
}

function StageLockControls({ stage, onUnlock, onRelock }) {
  return (
    <div className="col-span-3 flex items-center gap-2 flex-wrap">
      <StatusPill status={stage.status} />
      {stage.locked && (
        <button
          data-testid={`unlock-stage-${stage.id}`}
          onClick={(e) => { e.stopPropagation(); onUnlock(); }}
          title="Manually unlock this stage for managers"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap hover:opacity-80"
          style={{ backgroundColor: "#64748B22", color: "#64748B" }}
        >
          <Lock size={11} /> Locked — Unlock
        </button>
      )}
      {!stage.locked && stage.manual_unlocked && (
        <span data-testid={`manually-unlocked-${stage.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{ backgroundColor: "#14B8A622", color: "#14B8A6" }}>
          <Unlock size={11} /> Manually unlocked
        </span>
      )}
      {stage.manual_unlocked && (
        <button
          data-testid={`relock-stage-${stage.id}`}
          onClick={(e) => { e.stopPropagation(); onRelock(); }}
          title="Remove the manual unlock; the stage re-locks if the previous stage is incomplete"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap hover:opacity-80"
          style={{ backgroundColor: "#64748B22", color: "#64748B" }}
        >
          <Lock size={11} /> Re-lock
        </button>
      )}
    </div>
  );
}

function StageTitleBar({ stage, editingTitle, setEditingTitle, titleVal, setTitleVal, onSaveTitle, onDelete }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
      {editingTitle ? (
        <div className="flex items-center gap-2">
          <input data-testid={`stage-title-input-${stage.id}`} value={titleVal} onChange={(e) => setTitleVal(e.target.value)} className={fieldCls} style={{ borderColor: "#E5E7EB" }} />
          <button data-testid={`save-stage-title-${stage.id}`} onClick={onSaveTitle} className="rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ backgroundColor: "#0A1F44" }}>Save</button>
          <button onClick={() => { setEditingTitle(false); setTitleVal(stage.title); }} className="text-sm text-[#6B7280]">Cancel</button>
        </div>
      ) : (
        <button onClick={() => setEditingTitle(true)} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1A1A1A]"><Pencil size={14} /> Rename stage</button>
      )}
      <button data-testid={`delete-stage-${stage.id}`} onClick={onDelete} className="flex items-center gap-1.5 text-sm text-red-500 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /> Delete stage</button>
    </div>
  );
}

function PendingResourceList({ stageId, pending, setPending, uploading, onUpload }) {
  return (
    <div className="space-y-2 mb-3 p-2 rounded-lg" style={{ backgroundColor: "#FEF0E6" }} data-testid={`pending-resources-${stageId}`}>
      <p className="text-xs text-[#6B7280]">Assign a display name to each file (what managers see), then upload:</p>
      {pending.map((p) => (
        <div key={p.uid} className="flex items-center gap-2">
          <input value={p.name} onChange={(e) => setPending((arr) => arr.map((x) => x.uid === p.uid ? { ...x, name: e.target.value } : x))}
            className="flex-1 rounded px-2 py-1.5 text-sm border bg-white" style={{ borderColor: "#E5E7EB" }} placeholder="Display name (e.g. FAQ)" />
          <button onClick={() => setPending((arr) => arr.filter((x) => x.uid !== p.uid))} className="text-red-500 p-1"><X size={14} /></button>
        </div>
      ))}
      <button data-testid={`upload-resources-${stageId}`} onClick={onUpload} disabled={uploading}
        className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
        {uploading ? "Uploading..." : `Upload ${pending.length} file(s)`}
      </button>
    </div>
  );
}

function StageResourceList({ resources, onDelete }) {
  if (!resources.length) return <p className="text-sm text-[#9CA3AF]">No files.</p>;
  return (
    <div className="space-y-1">
      {resources.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 truncate"><FileText size={13} />{r.display_name || r.filename}</span>
          <div className="flex gap-1">
            <button data-testid={`download-resource-${r.id}`} onClick={() => downloadResource(r.file_id, r.filename)} className="p-1 rounded hover:bg-gray-100 text-[#6B7280]" title="Download"><Download size={14} /></button>
            <button onClick={() => onDelete(r.id)} className="p-1 rounded hover:bg-gray-100 text-red-500"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function StageVideoPanel({ stageId, videos, videoUrl, setVideoUrl, videoName, setVideoName, onAdd, onDelete }) {
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2 flex items-center gap-1.5"><Youtube size={14} style={{ color: "#F97316" }} /> Video Links (YouTube)</p>
      <div className="flex flex-wrap gap-2 mb-2">
        <input data-testid={`stage-video-url-${stageId}`} value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className={`${fieldCls} flex-1 min-w-[200px]`} style={{ borderColor: "#E5E7EB" }} />
        <input data-testid={`stage-video-name-${stageId}`} value={videoName} onChange={(e) => setVideoName(e.target.value)} placeholder="Display name (e.g. Toolbox Talk)" className={`${fieldCls} w-48`} style={{ borderColor: "#E5E7EB" }} />
        <button data-testid={`add-video-${stageId}`} onClick={onAdd} className="rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: "#0A1F44" }}>Add</button>
      </div>
      {(videos || []).length === 0 ? <p className="text-sm text-[#9CA3AF]">No videos.</p> :
        <div className="space-y-1">
          {videos.map((v) => (
            <div key={v.id} className="flex items-center justify-between text-sm">
              <a href={v.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 truncate text-[#F97316] hover:underline"><Youtube size={13} />{v.display_name}</a>
              <button onClick={() => onDelete(v.id)} className="p-1 rounded hover:bg-gray-100 text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>}
    </div>
  );
}

function useStageConfirm(stage, reload) {
  const [confirmAction, setConfirmAction] = useState(null); // "delete" | "unlock" | "relock"
  const runConfirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === "delete") {
      await api.delete(`/implementation/stages/${stage.id}`);
      toast.success("Stage deleted");
    } else if (action === "unlock") {
      await api.post(`/implementation/stages/${stage.id}/unlock`);
      toast.success("Stage unlocked for managers");
    } else {
      await api.post(`/implementation/stages/${stage.id}/relock`);
      toast.success("Stage re-locked");
    }
    reload();
  };
  return { confirmAction, setConfirmAction, runConfirm };
}

function StageConfirmDialog({ stage, confirmAction, onConfirm, onCancel }) {
  return (
    <ConfirmDialog
      open={!!confirmAction}
      message={{
        unlock: "The previous stage is not complete. Unlock this stage anyway?",
        relock: "Re-lock this stage? Managers will lose access until the previous stage is complete.",
        delete: "Delete this stage and its resources?",
      }[confirmAction] || ""}
      confirmLabel={{ unlock: "Unlock", relock: "Re-lock", delete: "Delete" }[confirmAction] || "Confirm"}
      onConfirm={onConfirm}
      onCancel={onCancel}
      testId={`stage-confirm-${stage.id}`}
    />
  );
}

function StageTitleManager({ stage, reload, onDelete }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState(stage.title);
  const saveTitle = async () => {
    if (!titleVal.trim()) { toast.error("Stage title required"); return; }
    await api.put(`/implementation/stages/${stage.id}`, { title: titleVal });
    toast.success("Stage renamed"); setEditingTitle(false); reload();
  };
  return (
    <StageTitleBar stage={stage} editingTitle={editingTitle} setEditingTitle={setEditingTitle}
      titleVal={titleVal} setTitleVal={setTitleVal} onSaveTitle={saveTitle} onDelete={onDelete} />
  );
}

function StageIntroPanel({ stage, reload }) {
  const [intro, setIntro] = useState(stage.introduction || "");
  const saveIntro = async () => { await api.put(`/implementation/stages/${stage.id}`, { introduction: intro }); toast.success("Introduction saved"); reload(); };
  return (
    <div className="rounded-lg border p-3 bg-white" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Introduction</p>
      <textarea data-testid={`stage-intro-${stage.id}`} value={intro} onChange={(e) => setIntro(e.target.value)} rows={4}
        placeholder="Instructions, context and guidance for managers…" className={`${fieldCls} w-full resize-y`} style={{ borderColor: "#E5E7EB" }} />
      <button data-testid={`save-intro-${stage.id}`} onClick={saveIntro} className="mt-2 flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: "#0A1F44" }}><Save size={14} /> Save Introduction</button>
    </div>
  );
}

function StageVideoManager({ stage, reload }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [videoName, setVideoName] = useState("");
  const addVideo = async () => {
    if (!videoUrl.trim()) { toast.error("Video URL required"); return; }
    await api.post(`/implementation/stages/${stage.id}/videos`, { url: videoUrl, display_name: videoName });
    toast.success("Video added"); setVideoUrl(""); setVideoName(""); reload();
  };
  const delVideo = async (id) => { await api.delete(`/implementation/stages/${stage.id}/videos/${id}`); reload(); };
  return (
    <StageVideoPanel stageId={stage.id} videos={stage.videos} videoUrl={videoUrl} setVideoUrl={setVideoUrl}
      videoName={videoName} setVideoName={setVideoName} onAdd={addVideo} onDelete={delVideo} />
  );
}

function StageResourcesPanel({ stage, reload }) {
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const delResource = async (id) => { await api.delete(`/implementation/stages/${stage.id}/resources/${id}`); reload(); };
  const pickFiles = (files) => {
    const arr = Array.from(files).map((f) => ({ uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file: f, name: f.name }));
    setPending((p) => [...p, ...arr]);
  };
  const uploadPending = async () => {
    if (!pending.length) return;
    setUploading(true);
    try {
      for (const p of pending) {
        const fd = new FormData(); fd.append("file", p.file); fd.append("display_name", p.name);
        await api.post(`/implementation/stages/${stage.id}/resources`, fd);
      }
      toast.success(`${pending.length} resource(s) uploaded`); setPending([]); reload();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  return (
    <div className="mt-3 rounded-lg border p-3 bg-white" style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Stage Resources</p>
        <button data-testid={`add-resource-${stage.id}`} onClick={() => fileRef.current.click()} className="flex items-center gap-1.5 text-sm text-[#F97316] font-medium"><Paperclip size={14} /> Add files</button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files.length) pickFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {pending.length > 0 && (
        <PendingResourceList stageId={stage.id} pending={pending} setPending={setPending} uploading={uploading} onUpload={uploadPending} />
      )}

      <StageResourceList resources={stage.resources || []} onDelete={delResource} />

      <StageVideoManager stage={stage} reload={reload} />
    </div>
  );
}

function StageHeaderRow({ stage, open, onToggle, onUnlock, onRelock }) {
  return (
    <div className="grid grid-cols-12 items-center px-4 py-3 gap-2 hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
      <div className="col-span-6 font-medium flex items-center gap-2 min-w-0">
        <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
        <Layers size={15} style={{ color: "#F97316" }} />
        <span className="truncate">{stage.title}</span>
      </div>
      <StageLockControls stage={stage} onUnlock={onUnlock} onRelock={onRelock} />
      <div className="col-span-3 text-sm text-[#6B7280]">{stage.has_resources ? `${stage.resources.length} resource(s)` : "None"}</div>
    </div>
  );
}

function StageRow({ stage, reload }) {
  const [open, setOpen] = useState(false);
  const { confirmAction, setConfirmAction, runConfirm } = useStageConfirm(stage, reload);

  return (
    <div className="border-t" style={{ borderColor: "#E5E7EB" }} data-testid={`admin-stage-${stage.id}`}>
      <StageConfirmDialog stage={stage} confirmAction={confirmAction} onConfirm={runConfirm} onCancel={() => setConfirmAction(null)} />
      <StageHeaderRow stage={stage} open={open} onToggle={() => setOpen((o) => !o)}
        onUnlock={() => setConfirmAction("unlock")} onRelock={() => setConfirmAction("relock")} />

      {open && (
        <div className="px-4 pb-5 pt-1 bg-[#F9FAFB]" onClick={(e) => e.stopPropagation()}>
          <StageTitleManager stage={stage} reload={reload} onDelete={() => setConfirmAction("delete")} />
          <StageIntroPanel stage={stage} reload={reload} />
          <StageResourcesPanel stage={stage} reload={reload} />
          <TaskManager stage={stage} reload={reload} />
        </div>
      )}
    </div>
  );
}

export default function AdminImplementation() {
  const { clients, active, setActive, status: clientsStatus, error: clientsError, retry: retryClients } = useActiveClients();
  const [stages, setStages] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");

  const load = useCallback(() => { if (active) api.get(`/clients/${active}/implementation/stages`).then(({ data }) => setStages(data)); }, [active]);
  useEffect(() => { load(); }, [load]);

  const addStage = async () => {
    if (!title.trim()) { toast.error("Stage title required"); return; }
    await api.post(`/clients/${active}/implementation/stages`, { title });
    toast.success("Stage created"); setTitle(""); setAdding(false); load();
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Implementation</h1>
      <p className="text-[#6B7280] text-sm mb-6">Manage implementation stages, resources and tasks per client.</p>

      <ClientTabs clients={clients} active={active} onChange={setActive} status={clientsStatus} error={clientsError} onRetry={retryClients} />

      {active && (
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-[#6B7280]">{stages.length} stage(s)</p>
            <button data-testid="add-stage-button" onClick={() => setAdding((a) => !a)}
              className="flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#F97316" }}>
              <Plus size={16} /> Create Stage
            </button>
          </div>

          {adding && (
            <div className="flex flex-wrap items-end gap-2 p-4 rounded-xl border mb-4 bg-white" style={{ borderColor: "#E5E7EB" }} data-testid="stage-form">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-[#6B7280] mb-1 block">Stage Title (e.g. Stage 1)</label>
                <input data-testid="stage-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
              </div>
              <button data-testid="stage-save-button" onClick={addStage} className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#0A1F44" }}>Save</button>
            </div>
          )}

          <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }}>
            <div className="grid grid-cols-12 px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: "#0A1F44", color: "#fff" }}>
              <div className="col-span-6">Stage</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-3">Resources</div>
            </div>
            {stages.length === 0 ? <p className="text-center py-12 text-[#6B7280]">No stages yet. Create one above.</p> :
              stages.map((s) => <StageRow key={s.id} stage={s} reload={load} />)}
          </div>
        </>
      )}
    </div>
  );
}
